import { Sidebar } from '@hssoft/design-system-shared-lib';
import { Box, styled } from '@mui/material';
import appVersion from '../app-version.json';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdminModeBarContainer } from '../shared/components/adminModeBar/adminModeBar.component.tsx';
import { AppSidebar } from '../shared/components/navigation/appSidebar.component.tsx';
// Date-picker
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import de from 'date-fns/locale/de';
import en from 'date-fns/locale/en-GB';
import fr from 'date-fns/locale/fr';
import it from 'date-fns/locale/it';
import { take } from 'rxjs';
// Nav icons
// import ProcurementIco from '@mui/icons-material/AddShoppingCart';
// import OfferIco from '@mui/icons-material/BusinessCenter';
import { BusinessCenter } from '@mui/icons-material';
import BarChartIcon from '@mui/icons-material/BarChart';
import ProductionIco from '@mui/icons-material/Factory';
import InvoiceIco from '@mui/icons-material/LocalAtm';
import DebitorIco from '@mui/icons-material/PendingActions';
import CustomerIco from '@mui/icons-material/Person';
import SettingsIco from '@mui/icons-material/Settings';
import OrderIco from '@mui/icons-material/ShoppingCartCheckout';
import ArticleIco from '@mui/icons-material/ViewInAr';
import { Outlet, useNavigation } from 'react-router-dom';

import { environment } from '../environment.ts';
import { C_Email_Msg_Doctype, C_Language } from '../graphql/generatedModel.ts';
import { invoiceDetailsState } from '../pages/invoice/list/states/invoiceDetails.state.ts';
import { offerDetailsState } from '../pages/offer/states/offerDetails/offerDetails.state.ts';
import { orderDetailsState } from '../pages/order/edit/states/orderDetails/orderDetails.state.ts';
import { AiAssistant } from '../shared/components/aiAssistant/aiAssistant.component.tsx';
import { Guard } from '../shared/components/guard/guard.component.tsx';
import { transitionService } from '../shared/components/loading/service/transition.service.ts';
import { Transition } from '../shared/components/loading/transition.component';
import { TopBar } from '../shared/components/navigation/components/topBar/topBar.component.tsx';
import { snackbarService } from '../shared/components/snackbar/service/snackbar.service.ts';
import { localeFormatterHelper } from '../shared/helpers/formatter/localeFormatter.helper.ts';
import { storageHelper } from '../shared/helpers/storage';
import {
  companySwitchedPopup,
  loggedOutPopup,
  useGlobalState,
} from '../shared/hooks/useGlobalState.tsx';
import useLanguage from '../shared/hooks/useLanguage.tsx';
import { responseHandler } from '../shared/responseHandler/responseHandler.ts';
import { companyConfigService } from '../shared/services/companyConfig/companyConfig.service.ts';
import { Navigate } from '../shared/services/navigate/components/navigate.component.tsx';
import {
  reportsService,
  SendEmailWithDocumentPlusParamsRes,
} from '../shared/services/reports/reports.service.ts';
import { AiBigButton } from '../shared/components/aiAssistant/components/aiBigButton.component.tsx';
import { AiSmallButton } from '../shared/components/aiAssistant/components/aiSmallButton.component.tsx';
import {
  authService,
  APP_MODULES,
  IUserAccessParams,
  initUserAccessParams,
} from '../shared/services/auth/auth.service.ts';
import { checkModuleAccess } from '../routes/helper.accessParams.ts';

const RootLayout: FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [openAIAssistant, setOpenAIAssistant] = useState<boolean>(false);
  const [showAiBigButton, setShowAiBigButton] = useState<boolean>(true);
  const [showAiSmallButton, setShowAiSmallButton] = useState<boolean>(false);
  // Seed from the cache warmed by `ProtectedRoute` (which gates this layout) so
  // the first paint already reflects `isAdminMode` and module access. Otherwise
  // the admin bar / sidebar mount with defaults and only correct once the
  // `hasAccess()` force-refresh below resolves (~1-2s), shifting the top bar and
  // left menu down — the WA-3494 flicker.
  const [userAccessParams, setUserAccessParams] = useState<IUserAccessParams>(
    () => authService.sub.getCachedUserAccessParamsSync() ?? initUserAccessParams,
  );
  const [companyName, setCompanyName] = useState<string | undefined>(undefined);
  const [companyGUID, setCompanyGUID] = useState<string | undefined>(undefined);
  const { changeLanguageHandler } = useLanguage();
  const appEnv = import.meta.env.VITE_REACT_APP_ENV as keyof typeof environment;
  const { state } = useNavigation();
  const { i18n, t } = useTranslation();

  const localeFns = useMemo(() => {
    switch (i18n.language) {
      case 'de':
        return de;
      case 'en':
        return en;
      case 'fr':
        return fr;
      case 'it':
        return it;
      default:
        return de;
    }
  }, [i18n.language]);

  useEffect(() => {
    companyConfigService
      .getConfigs()
      .pipe(take(1))
      .subscribe((v) => {
        localeFormatterHelper.setCompanyConfig(v);
        // Optional-chain `v` itself, not just `currentUser` (WA-3750): on a
        // session expiry `getConfigs()` emits its `errorReturnType` rather than a
        // real config, and `currentUser` is nullable even on a successful
        // response — so neither level is guaranteed while the app is redirecting
        // to /login. A throw here lands inside gqlClient's `subscriber.error()`
        // call stack as a brand-new error, i.e. WITHOUT the __sentryIgnore flag
        // `beforeSend` needs, which is how it auto-filed WAWIASSIST-23B.
        setCompanyName(v?.currentUser?.company ?? undefined);
        let language: C_Language = C_Language.L2_GERMAN;

        if (appEnv === 'production' || appEnv === 'staging') {
          language = v?.currentUser?.userLanguageId || C_Language.L2_GERMAN;
        } else if (appEnv === 'development') {
          language =
            storageHelper.local.getItem('sys_data.language') ||
            v?.currentUser?.userLanguageId ||
            C_Language.L2_GERMAN;
        }
        changeLanguageHandler(language);
      });

    const userAccessSub = authService.sub.hasAccess().subscribe((accessParams) => {
      setUserAccessParams(accessParams);
    });

    const companyGuidSub = authService.sub
      .customerGUID()
      .pipe(take(1))
      .subscribe({
        next: (guid) => {
          setCompanyGUID(guid ? guid.toUpperCase() : undefined);
        },
        // A failed session read must not become an unhandled RxJS error: that
        // is what auto-filed WA-3800 (Sentry WAWIASSIST-246) from this very
        // subscription. The layout already renders with no company GUID.
        error: () => setCompanyGUID(undefined),
      });

    const emailSendingNotificationsSub = reportsService.sub
      .sendEmailWithDocument()
      .pipe(
        responseHandler<SendEmailWithDocumentPlusParamsRes>({
          customErrorHandler: () => 'common.problem_while_sending_email',
          errorReturnType: {
            sentMessages: [],
            unsentMessages: [],
            documentId: '',
            docType: C_Email_Msg_Doctype.EML_ORDER,
          },
          quite: true,
        }),
      )
      .subscribe((res) => {
        let upgradableService;
        const { documentId, docType } = res;
        switch (docType) {
          case C_Email_Msg_Doctype.EML_ORDER:
            upgradableService = orderDetailsState;
            break;
          case C_Email_Msg_Doctype.EML_INVOICE:
            upgradableService = invoiceDetailsState;
            break;
          case C_Email_Msg_Doctype.EML_OFFER:
            upgradableService = offerDetailsState;
            break;
          default:
            upgradableService = null;
        }
        snackbarService.pub.hide('emailWithReportLoading');
        if (res.sentMessages?.length && !res.unsentMessages?.length) {
          snackbarService.pub.show({
            content: t('common.email_sent_successfully', { count: res.sentMessages.length }),
            type: 'success',
          });
          if (upgradableService && documentId) {
            const datetimeOfLastEmail = res.sentMessages[res.sentMessages.length - 1].timestamp;
            upgradableService.pub.updateLastEmailDetails(datetimeOfLastEmail, String(documentId));
          }
        }
        if (res.sentMessages?.length && res.unsentMessages?.length) {
          snackbarService.pub.show({
            content: `${t('common.emails_sent_successfully', {
              successCount: res.sentMessages?.length,
            })}\n${t('common.emails_failed_to_send', {
              failureCount: res.unsentMessages?.length,
            })}`,
            type: 'warning',
          });
          if (upgradableService && documentId) {
            const datetimeOfLastEmail = res.sentMessages[res.sentMessages.length - 1].timestamp;
            upgradableService.pub.updateLastEmailDetails(datetimeOfLastEmail, String(documentId));
          }
        }
        if (!res.sentMessages?.length && res.unsentMessages?.length) {
          snackbarService.pub.show({
            content: t('common.problem_while_sending_email', { count: res.unsentMessages.length }),
            type: 'error',
          });
        }
      });
    return () => {
      userAccessSub.unsubscribe();
      companyGuidSub.unsubscribe();
      emailSendingNotificationsSub.unsubscribe();
    };
  }, []);

  useGlobalState();

  const openAIAssistantHandler = () => {
    if (!openAIAssistant) {
      setShowAiBigButton(false);
    } else {
      setShowAiBigButton(true);
    }
    setOpenAIAssistant((value) => !value);
  };

  const keyBindAIAssistantHandler = (e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'i') {
      e.preventDefault();
      openAIAssistantHandler();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', keyBindAIAssistantHandler, false);
    return () => {
      window.removeEventListener('keydown', keyBindAIAssistantHandler, false);
    };
  }, []);

  useEffect(() => {
    if (state === 'idle') {
      setLoading(false);
      transitionService.pub.on();
    }
    if (state === 'loading') {
      setLoading(true);
    }
  }, [state]);

  const hasModuleAccess = (module: APP_MODULES): boolean => {
    return checkModuleAccess(userAccessParams, module);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeFns}>
      <Navigate />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflowX: 'auto',
          overflowY: 'hidden',
        }}
      >
        {userAccessParams.isAdminMode && (
          <AdminModeBarContainer
            companyName={companyName}
            companyGUID={companyGUID}
            appVersion={appVersion.version}
          />
        )}
        <SRootLayout>
          <AppSidebar>
            <Guard module='customer'>
              <Sidebar.Item
                id='customer'
                href='/customer/general'
                matchPath='/customer'
                icon={<CustomerIco />}
                label={t('customer.customer')}
                disabled={!hasModuleAccess(APP_MODULES.CUSTOMER)}
              />
            </Guard>
            <Guard module='article'>
              <Sidebar.Item
                id='article'
                href='/article/general'
                matchPath='/article'
                icon={<ArticleIco />}
                label={t('article.article')}
                disabled={!hasModuleAccess(APP_MODULES.ARTICLE)}
              />
            </Guard>
            <Guard module='offer'>
              <Sidebar.Item
                id='offer'
                href='/offer'
                icon={<BusinessCenter />}
                label={t('offer.offer')}
                disabled={!hasModuleAccess(APP_MODULES.OFFER)}
              />
            </Guard>
            <Guard module='order'>
              <Sidebar.Group
                id='order'
                icon={<OrderIco />}
                label={t('order.order')}
                disabled={!hasModuleAccess(APP_MODULES.ORDER)}
              >
                <Guard module='order' componentName='createOrder'>
                  <Sidebar.Item
                    id='order.create'
                    href='/order/create'
                    label={t('order.create_order')}
                    testId='create-order-sidebar-item'
                  />
                </Guard>
                <Guard module='order' componentName='editOrder'>
                  <Sidebar.Item id='order.edit' href='/order/edit' label={t('order.edit_order')} />
                </Guard>
                <Guard module='order' componentName='orderOverview'>
                  <Sidebar.Item
                    id='order.overview'
                    href='/order/overview'
                    label={t('order.overview')}
                  />
                </Guard>
              </Sidebar.Group>
            </Guard>
            <Guard module='production'>
              <Sidebar.Group
                id='production'
                icon={<ProductionIco />}
                label={t('production.production')}
                disabled={!hasModuleAccess(APP_MODULES.PRODUCTION)}
              >
                <Guard module='production' componentName='printJobs'>
                  <Sidebar.Item
                    id='production.print-jobs'
                    href='/production/print-jobs/production'
                    matchPath='/production/print-jobs'
                    label={t('production.print_jobs')}
                  />
                </Guard>
                <Guard module='production' componentName='editPrintList'>
                  <Sidebar.Item
                    id='production.edit-print-list'
                    href='/production/edit-print-list/general'
                    matchPath='/production/edit-print-list'
                    label={t('production.edit_print_list')}
                  />
                </Guard>
                <Guard module='production' componentName='stockOfFreezer'>
                  <Sidebar.Item
                    id='production.stock-of-freezer'
                    href='/production/stock-of-freezer'
                    label={t('production.stock_of_freezer')}
                  />
                </Guard>
                <Guard module='production' componentName='toolboxExport'>
                  <Sidebar.Item
                    id='production.toolbox'
                    href='/production/toolbox'
                    label={t('production.toolbox')}
                  />
                </Guard>
              </Sidebar.Group>
            </Guard>
            <Guard module='invoice'>
              <Sidebar.Group
                id='invoice'
                icon={<InvoiceIco />}
                label={t('invoice.invoice')}
                disabled={!hasModuleAccess(APP_MODULES.INVOICE)}
              >
                <Guard module='invoice' componentName='createInvoice'>
                  <Sidebar.Item
                    id='invoice.create-invoice'
                    href='/invoice/create-invoice'
                    label={t('invoice.create_invoice')}
                  />
                </Guard>
                <Guard module='invoice' componentName='invoiceList'>
                  <Sidebar.Item
                    id='invoice.invoices-list'
                    href='/invoice/invoices-list'
                    label={t('invoice.invoices_list')}
                  />
                </Guard>
              </Sidebar.Group>
            </Guard>
            <Guard module='debitor'>
              <Sidebar.Group
                id='debitor'
                icon={<DebitorIco />}
                label={t('debitor.debitor')}
                disabled={!hasModuleAccess(APP_MODULES.DEBITOR)}
              >
                <Guard module='debitor' componentName='openDebts'>
                  <Sidebar.Item
                    id='debitor.open-debts'
                    href='/debitor/open-debts'
                    label={t('debitor.openDebts')}
                  />
                </Guard>
                <Guard module='debitor' componentName='payments'>
                  <Sidebar.Item
                    id='debitor.payments'
                    href='/debitor/payments'
                    label={t('debitor.payments')}
                  />
                </Guard>
                <Guard module='debitor' componentName='importIso'>
                  <Sidebar.Item
                    id='debitor.import-iso'
                    href='/debitor/import-iso'
                    label={t('debitor.importIso')}
                  />
                </Guard>
              </Sidebar.Group>
            </Guard>
            <Guard module='statistic'>
              <Sidebar.Group
                id='statistic'
                icon={<BarChartIcon />}
                label={t('statistic.statistic')}
                disabled={!hasModuleAccess(APP_MODULES.STATISTIC)}
              >
                <Guard module='statistic' componentName='orderStatistic'>
                  <Sidebar.Item
                    id='statistic.order-statistic'
                    href='/statistic/order-statistic'
                    label={t('statistic.order_statistic')}
                  />
                </Guard>
                <Guard module='statistic' componentName='productionStatistic'>
                  <Sidebar.Item
                    id='statistic.production-statistic'
                    href='/statistic/production-statistic'
                    label={t('statistic.production_statistic')}
                  />
                </Guard>
              </Sidebar.Group>
            </Guard>
            <Guard module='settings'>
              <Sidebar.Item
                id='settings'
                href='/settings/company/company'
                matchPath='/settings'
                icon={<SettingsIco />}
                label={t('common.settings')}
                disabled={!hasModuleAccess(APP_MODULES.OPTIONS)}
              />
            </Guard>
          </AppSidebar>
          <Box
            sx={{
              pl: (theme) => theme.nav.width,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <TopBar />
            <Transition show={loading}>
              <Outlet />
            </Transition>
          </Box>
          <Guard module='layout' componentName='assistant'>
            <AiAssistant open={openAIAssistant} onOpen={openAIAssistantHandler} />
            <AiBigButton
              show={showAiBigButton}
              onOpenAIAssistant={openAIAssistantHandler}
              onShowAISmallButton={() => {
                setShowAiBigButton(false);
                setShowAiSmallButton(true);
              }}
            />
            <AiSmallButton
              show={showAiSmallButton}
              onShowAiBigButton={() => {
                setShowAiBigButton(true);
                setShowAiSmallButton(false);
              }}
            />
          </Guard>
        </SRootLayout>
      </Box>
      <loggedOutPopup.Component />
      <companySwitchedPopup.Component />
    </LocalizationProvider>
  );
};
export default RootLayout;

const SRootLayout = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  position: 'relative',
  top: 0,
  left: 0,
  opacity: 1,
  transition: 'opacity 1s',
  maxWidth: '1980px',
  minWidth: '1366px',
  width: '100vw',
  flex: 1,
  minHeight: 0,
  margin: '0 auto',
  borderLeft: `1px solid ${theme.palette.divider}`,
  borderRight: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
}));
